import { useMemo } from "react";
import type { FeatureContainer, FieldGroup, FormLevelFeature } from "@/entities/form-metadata";
import { FormLevelFeatureType, ResponseItemType, useMetadataQuery } from "@/entities/form-metadata";
import { useCollapsibleViewContext } from "./CollapsibleViewContext";

export const useCollapsibleView = () => {
    const { isCollapsed } = useCollapsibleViewContext();
    const { data: formMetadata } = useMetadataQuery();

    const { collapsibleFeature, collapsibleStart, collapsibleEnd } = useMemo(
        () =>
            formMetadata.features.reduce(
                (acc, feature) => {
                    if (feature.featureType === FormLevelFeatureType.CollapsibleView) acc.collapsibleFeature = feature;
                    else if (feature.featureType === FormLevelFeatureType.CollapsibleStart)
                        acc.collapsibleStart = feature;
                    else if (feature.featureType === FormLevelFeatureType.CollapsibleEnd) acc.collapsibleEnd = feature;
                    return acc;
                },
                {} as {
                    collapsibleFeature?: FormLevelFeature;
                    collapsibleStart?: FormLevelFeature;
                    collapsibleEnd?: FormLevelFeature;
                },
            ),
        [formMetadata.features],
    );

    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- TODO
    const { collapsibleStartIndex, collapsibleEndIndex } = useMemo(() => {
        let featureIndex: number | undefined;
        let startIndex: number | undefined;
        let endIndex: number | undefined;

        formMetadata.fieldGroupsAndFeatureContainers.forEach((groupOrFeature, i) => {
            if (groupOrFeature.type === ResponseItemType.FeatureContainer) {
                if (collapsibleFeature?.UUID && groupOrFeature.ids.includes(collapsibleFeature.UUID)) {
                    featureIndex = i;
                }
                if (collapsibleStart?.UUID && groupOrFeature.ids.includes(collapsibleStart.UUID)) {
                    startIndex = i;
                }
                if (collapsibleEnd?.UUID && groupOrFeature.ids.includes(collapsibleEnd.UUID)) {
                    endIndex = i;
                }
            }
        });

        if (collapsibleFeature && !collapsibleEnd) {
            endIndex = formMetadata.fieldGroupsAndFeatureContainers.length - 1;
        }

        if (startIndex && featureIndex === startIndex) startIndex += 1;

        return {
            collapsibleFeatureIndex: featureIndex,
            collapsibleStartIndex: startIndex,
            collapsibleEndIndex: endIndex,
        };
    }, [formMetadata.fieldGroupsAndFeatureContainers, collapsibleFeature, collapsibleStart, collapsibleEnd]);

    const sections = useMemo(() => {
        const result: (FeatureContainer | FieldGroup)[] = [];

        formMetadata.fieldGroupsAndFeatureContainers.forEach((item, i) => {
            if (collapsibleStartIndex === undefined || i < collapsibleStartIndex) {
                result.push(item);
            } else if (!isCollapsed && collapsibleEndIndex !== undefined && i <= collapsibleEndIndex) {
                result.push(item);
            } else if (collapsibleEndIndex !== undefined && i > collapsibleEndIndex) {
                result.push(item);
            }
        });

        return result;
    }, [formMetadata.fieldGroupsAndFeatureContainers, collapsibleStartIndex, collapsibleEndIndex, isCollapsed]);

    return sections;
};
